/**
 * 封装 axios
 */
import axios from 'axios'
import { ElMessage } from 'element-plus'

import store from '@/store'
import router from '@/router'
import Config from '@/config'
import autoJump from '@/lin/util/auto-jump'
import ErrorCode from '@/config/error-code'
import { getToken, saveAccessToken, removeToken } from '@/lin/util/token'

const config = {
  baseURL: Config.baseURL || '',
  timeout: 5 * 1000, // 请求超时时间设置
  crossDomain: true,
  withCredentials: true,
  // 定义可获得的http响应状态码
  // return true、设置为null或者undefined，promise将resolved,否则将rejected
  validateStatus(status) {
    return status >= 200 && status < 510
  },
}

/**
 * 错误码是否是refresh相关
 * @param { number } code 错误码
 */
function refreshTokenException(code) {
  const codes = [10000, 10042, 10050, 10052, 10012]
  return codes.includes(code)
}

// 创建请求实例
const _axios = axios.create(config)

_axios.interceptors.request.use(
  originConfig => {
    // 有 API 请求重新计时
    autoJump(router)

    const reqConfig = { ...originConfig }

    // step1: 容错处理
    if (!reqConfig.url) {
      console.error('request need url')
    }

    reqConfig.method = reqConfig.method.toLowerCase() // 大小写容错

    // 参数容错
    if (reqConfig.method === 'get') {
      if (!reqConfig.params) {
        reqConfig.params = reqConfig.data || {}
      }
    } else if (reqConfig.method === 'post') {
      if (!reqConfig.data) {
        reqConfig.data = reqConfig.params || {}
      }

      // 检测是否包含文件类型, 若包含则进行 formData 封装
      let hasFile = false
      Object.keys(reqConfig.data).forEach(key => {
        if (typeof reqConfig.data[key] === 'object') {
          const item = reqConfig.data[key]
          if (item instanceof FileList || item instanceof File || item instanceof Blob) {
            hasFile = true
          }
        }
      })

      // 检测到存在文件使用 FormData 提交数据
      if (hasFile) {
        const formData = new FormData()
        Object.keys(reqConfig.data).forEach(key => {
          formData.append(key, reqConfig.data[key])
        })
        reqConfig.data = formData
      }
    }

    // step2: permission 处理
    if (reqConfig.url === 'cms/user/refresh') {
      const refreshToken = getToken('admin_refresh_token')
      if (refreshToken) {
        reqConfig.headers.Authorization = refreshToken
      }
    } else {
      const accessToken = getToken('admin_access_token')
      if (accessToken) {
        reqConfig.headers.Authorization = accessToken
      }
    }

    return reqConfig
  },
  error => Promise.reject(error),
)

// Add a response interceptor
_axios.interceptors.response.use(
  res => {
    if (res.config.responseType === 'blob') {
      return res.data
    }

    if (res.status.toString().charAt(0) === '2') {
      const { code, data, msg, count } = res.data

      if (code === 0) {
        return { data, count, msg }
      }

      if (code === 401) {
        ElMessage.error(msg || '登录已过期，请重新登录')
        removeToken()
        store.commit('REMOVE_LOGGED_IN')
        router.push('/login')
        return Promise.reject(res)
      }

      if (code === 406) {
        ElMessage.error(msg || 'token无效，请重新登录')
        removeToken()
        store.commit('REMOVE_LOGGED_IN')
        router.push('/login')
        return Promise.reject(res)
      }

      if (code === -1 || code === -2 || code === -3 || code === -4) {
        ElMessage.error(msg || '操作失败')
        return Promise.reject(res)
      }

      if (res.config.handleError) {
        return Promise.reject(res)
      }

      ElMessage.error(msg || '请求失败')
      return Promise.reject(res)
    }

    const { code, message, msg } = res.data

    if (res.status === 403) {
      removeToken()
      store.commit('REMOVE_LOGGED_IN')
      router.push('/403')
      return Promise.reject(res)
    }

    let tipMessage = ''

    if (refreshTokenException(code)) {
      setTimeout(() => {
        store.dispatch('loginOut')
        const { origin } = window.location
        window.location.href = origin
      }, 1500)
      return Promise.resolve(null)
    }
    if (code === 10041 || code === 10051) {
      const cache = {}
      if (cache.url !== res.config.url) {
        cache.url = res.config.url
        return _axios('cms/user/refresh').then(refreshResult => {
          saveAccessToken(refreshResult.access_token)
          return _axios(res.config)
        })
      }
    }

    if (res.config.handleError) {
      return Promise.reject(res)
    }

    if (Config.useFrontEndErrorMsg && !res.config.showBackend) {
      const errorArr = Object.entries(ErrorCode).filter(v => v[0] === code.toString())
      if (errorArr.length > 0 && errorArr[0][1] !== '') {
        ;[[, tipMessage]] = errorArr
      } else {
        tipMessage = ErrorCode['777']
      }
    }

    if (typeof message === 'string') {
      tipMessage = message
    } else if (typeof msg === 'string') {
      tipMessage = msg
    }
    if (Object.prototype.toString.call(message) === '[object Object]') {
      ;[tipMessage] = Object.values(message).flat()
    }
    if (Object.prototype.toString.call(message) === '[object Array]') {
      ;[tipMessage] = message
    }
    ElMessage.error(tipMessage)
    return Promise.reject(res)
  },
  error => {
    if (!error.response) {
      ElMessage.error('请检查 API 是否异常')
      console.log('error', error)
    }

    if (error.code === 'ECONNABORTED' && error.message.indexOf('timeout') !== -1) {
      ElMessage.warning('请求超时')
    }
    return Promise.reject(error)
  },
)

// 导出常用函数

/**
 * @param {string} url
 * @param {object} data
 * @param {object} params
 */
export function post(url, data = {}, params = {}) {
  return _axios({
    method: 'post',
    url,
    data,
    params,
  })
}

/**
 * @param {string} url
 * @param {object} params
 */
export function get(url, params = {}) {
  return _axios({
    method: 'get',
    url,
    params,
  })
}

/**
 * @param {string} url
 * @param {object} data
 * @param {object} params
 */
export function put(url, data = {}, params = {}) {
  return _axios({
    method: 'put',
    url,
    params,
    data,
  })
}

/**
 * @param {string} url
 * @param {object} params
 */
export function _delete(url, params = {}) {
  return _axios({
    method: 'delete',
    url,
    params,
  })
}

export default _axios
